import { Fragment } from "react"

import { getImageProps } from "next/image"

import type { HeroSlide } from "@/components/marketplace/hero/hero-carousel"
import { HeroReel } from "@/components/marketplace/hero/hero-reel"
import { Heading, LinkButton, Text } from "@/components/ui"
import { CoverLink } from "@/components/ui/cover-link"
import { TileImage } from "@/components/ui/tile-image"

const WORD_STAGGER_MS = 55

// CSS-swapped eager images download BOTH variants on every viewport; a <picture>
// media split fetches exactly one. 1008px is the viewport proxy for the 63rem
// @stage container where the stage turns landscape.
const HeroArtDirected = ({ tall, wide, alt, priority }: { tall: string; wide: string; alt: string; priority: boolean }) => {
  const { props: wideProps } = getImageProps({ src: wide, alt, fill: true, sizes: "96vw" })
  const { props: tallProps } = getImageProps({ src: tall, alt, fill: true, sizes: "100vw", loading: priority ? "eager" : "lazy" })
  return (
    <picture>
      <source media="(min-width: 1008px)" srcSet={wideProps.srcSet} sizes={wideProps.sizes} />
      <img {...tallProps} alt={alt} fetchPriority={priority ? "high" : undefined} className="absolute inset-0 size-full object-cover" />
    </picture>
  )
}

// The inter-word space lives BETWEEN the inline-block spans as its own text node:
// a space inside an inline-block is trailing whitespace and the browser strips it.
const Words = ({ text, from = 0 }: { text: string; from?: number }) => (
  <>
    {text.split(" ").map((word, index) => (
      <Fragment key={`${word}-${index}`}>
        {index > 0 ? " " : null}
        <span className="motion-safe:animate-rise-in inline-block" style={{ animationDelay: `${(from + index) * WORD_STAGGER_MS}ms` }}>
          {word}
        </span>
      </Fragment>
    ))}
  </>
)

const CompactCopy = ({ slide }: { slide: HeroSlide }) => (
  <div className="@stage:max-w-130 @stage:gap-300 flex max-w-1/2 flex-col items-start gap-150 text-left">
    <Heading as="h1" variant="md" family="hero" tone="inverted" balance className="text-compact-hero-title @stage:text-display-lg">
      <span className="block">{slide.title}</span>
      {slide.accent && <span className="block">{slide.accent}</span>}
    </Heading>
    {slide.sub && (
      <Text as="p" variant="bodySm" family="text" tone="inverted" className="text-body-xs motion-safe:animate-rise-in animation-delay-200 @stage:text-body-lg">
        {slide.sub}
      </Text>
    )}
    {slide.cta && (
      <LinkButton
        href={slide.ctaHref}
        variant="primary"
        size="sm"
        className="bg-surface-paper text-brand hover:bg-surface-paper/90 motion-safe:animate-rise-in animation-delay-300 @stage:h-12 @stage:px-800 @stage:text-body-lg pointer-events-auto mt-100 font-semibold"
      >
        {slide.cta}
      </LinkButton>
    )}
  </div>
)

const PhotoCopy = ({ slide }: { slide: HeroSlide }) => (
  <div className="@stage:items-start @stage:text-left flex max-w-130 flex-col items-center gap-500 text-center">
    <Heading as="h1" variant="heroSplit" family="hero" tone="inverted" balance>
      <span className="text-fg-inverted block">
        <Words text={slide.title} />
      </span>
      {slide.accent && (
        <span className="text-brand block">
          <Words text={slide.accent} from={slide.title.split(" ").length} />
        </span>
      )}
    </Heading>
    {slide.sub && (
      <Text as="p" variant="body" family="text" tone="inverted" className="motion-safe:animate-rise-in animation-delay-200 @stage:text-lede max-w-90 font-normal">
        {slide.sub}
      </Text>
    )}
    {/* CTA: white pill on phones (over the photo), Nelo-blue from @stage (in the copy
        column) — the @stage classes override the primary variant's blue at desktop. */}
    {slide.cta && (
      <div className="motion-safe:animate-rise-in animation-delay-300 @stage:w-auto mt-200 flex w-full">
        <LinkButton
          href={slide.ctaHref}
          variant="primary"
          size="md"
          fullWidth
          className="bg-surface-paper text-brand hover:bg-surface-paper/90 @stage:bg-brand @stage:text-fg-inverted @stage:hover:bg-brand-hover @stage:w-auto pointer-events-auto font-normal"
        >
          {slide.cta}
        </LinkButton>
      </div>
    )}
  </div>
)

const HeroPhotoFrame = ({ slide, priority, compact }: { slide: HeroSlide; priority: boolean; compact: boolean }) => {
  const tall = slide.image ?? slide.imageWide
  const wide = slide.imageWide ?? slide.image

  return (
    <div
      data-nelo-serve={slide.serveId}
      className={`press-banner bg-surface-muted rounded-12x @stage:aspect-auto relative isolate overflow-hidden ${compact ? "@stage:min-h-96 grid grid-cols-1" : "@stage:h-hero-stage aspect-2/3"}`}
    >
      {compact && <span aria-hidden className="@stage:hidden col-start-1 row-start-1 aspect-video" />}
      {tall && tall === wide && <TileImage src={tall} alt={slide.imageAlt ?? ""} fill sizes="(min-width: 1008px) 96vw, 100vw" priority={priority} className="object-cover" />}
      {tall && wide && tall !== wide && <HeroArtDirected tall={tall} wide={wide} alt={slide.imageAlt ?? ""} priority={priority} />}

      <CoverLink href={slide.cta ? slide.ctaHref : undefined} />
      {!compact && <span aria-hidden className="bg-scrim-bottom @stage:bg-scrim-photo-h pointer-events-none absolute inset-0" />}
      {compact ? (
        <div className="@stage:px-1200 @stage:py-800 pointer-events-none relative col-start-1 row-start-1 flex flex-col justify-center px-500 py-400">
          <CompactCopy slide={slide} />
        </div>
      ) : (
        <div className="@stage:h-full @stage:justify-center @stage:px-1200 @stage:py-1200 pointer-events-none relative flex h-full flex-col justify-end px-600 py-800">
          <PhotoCopy slide={slide} />
        </div>
      )}
    </div>
  )
}

export const HeroPhoto = ({ slides, priority = false, compact = false }: { slides: HeroSlide[]; priority?: boolean; compact?: boolean }) => {
  if (slides.length === 0) return null

  const frames = slides.map((slide, index) => <HeroPhotoFrame key={index} slide={slide} priority={priority && index === 0} compact={compact} />)

  return (
    <div className="@container">
      <section className="mx-gut">{frames.length === 1 ? frames : <HeroReel>{frames}</HeroReel>}</section>
    </div>
  )
}
